import { useCallback, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';


function useUrlState() {
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();

  /**
   * SECURITY NOTES (WINZ Portal)
   * - Mitigate open redirect by strictly validating pathname (no protocols, no protocol-relative, canonical form)
   * - Preserve existing public contract: currentUrl.search has NO leading '?', includes hash property
   * - Allow 'url' query parameter (tests rely on it) but neutralize javascript:, data:, vbscript:, and protocol-relative attempts
   * - Only allow safe URL schemes in query parameter values: http, https, and a limited set of benign schemes (mailto, tel)
   */
  
  // Allowlist of safe schemes for values that look like absolute URLs.
  const SAFE_SCHEMES = new Set(['http', 'https', 'mailto', 'tel']);

  // Returns true if value contains a scheme and it is NOT in the allowlist or is an explicitly dangerous scheme.
  const hasDangerousScheme = (val) => {
    if (!val) return false;
    const trimmed = String(val).trim();
    // Explicitly block known dangerous schemes and protocol-relative URLs
    const lower = trimmed.toLowerCase();
    if (lower.startsWith('javascript:') || lower.startsWith('data:') || lower.startsWith('vbscript:') || lower.startsWith('//')) {
      return true;
    }
    // Generic scheme pattern: <alpha>[<alnum+.->]*:
    const m = trimmed.match(/^[a-zA-Z][a-zA-Z0-9+.-]*:/);
    if (!m) return false; // no scheme present
    const scheme = m[0].slice(0, -1).toLowerCase();
    return !SAFE_SCHEMES.has(scheme);
  };

  const canonicalizePath = (raw) => {
    if (!raw || typeof raw !== 'string') return '/';
    let path = raw.startsWith('/') ? raw : '/' + raw;
    if (path.includes('://') || path.startsWith('//')) return '/';
    path = path.replace(/[\u0000-\u001F\u007F]/g, '');
    const segs = [];
    for (const seg of path.split('/')) {
      if (!seg || seg === '.') continue;
      if (seg === '..') { segs.pop(); continue; }
      segs.push(seg);
    }
    path = '/' + segs.join('/');
    return path || '/';
  };

  const sanitizeSearch = (rawSearch) => {
    if (!rawSearch || typeof rawSearch !== 'string') return '';
    const noQ = rawSearch.startsWith('?') ? rawSearch.slice(1) : rawSearch;
    const params = new URLSearchParams(noQ);
    const safe = new URLSearchParams();
    for (const [k, v] of params.entries()) {
      const val = v || '';
      // Drop values that contain an unsafe or disallowed scheme (includes javascript:, data:, vbscript:, and protocol-relative)
      if (hasDangerousScheme(val)) continue;
      // Keep length reasonable
      safe.set(k, val.slice(0, 1000));
    }
    return safe.toString(); // no leading '?'
  };

  const sanitizeHash = (rawHash) => {
    if (!rawHash || typeof rawHash !== 'string') return '';
    const h = rawHash.startsWith('#') ? rawHash : `#${rawHash}`;
    if (/javascript:|data:|vbscript:/i.test(h)) return '';
    return h.replace(/[^#A-Za-z0-9/_\-.%~@]/g, '-').slice(0, 200);
  };

  const sanitizedLocation = useMemo(() => ({
    pathname: canonicalizePath(location.pathname),
    search: sanitizeSearch(location.search), // WITHOUT leading '?'
    hash: sanitizeHash(location.hash)
  }), [location.pathname, location.search, location.hash]);

  const getQueryParams = useCallback(() => {
    const out = {};
    const sp = new URLSearchParams(sanitizedLocation.search);
    for (const [k, v] of sp.entries()) out[k] = v;
    return out;
  }, [sanitizedLocation.search]);

  const updateQueryParams = useCallback((newParams, options = {}) => {
    // Use LIVE location.search (sanitized) instead of memoized value so mutations to the mock/location
    // object between calls (without re-render) are still respected (supports rapid successive updates test).
    const liveSanitized = sanitizeSearch(location.search || '');
    const base = new URLSearchParams(liveSanitized);
    for (const [k, v] of Object.entries(newParams)) {
      if (v === undefined || v === '' || (Array.isArray(v) && v.length === 0)) {
        base.delete(k);
        continue;
      }
      const strVal = String(v);
      // Drop any value with dangerous or disallowed schemes (XSS/redirect mitigation)
      if (hasDangerousScheme(strVal)) continue;
      base.set(k, strVal.slice(0, 1000));
    }
    // Apply the updated query params using router's search params API
    // Respect replace semantics; other navigate options like `state` are not applicable here
    setSearchParams(base, { replace: options?.replace === true });
  }, [location.search, setSearchParams]);

  return {
    getQueryParams,
    updateQueryParams,
    currentUrl: sanitizedLocation
  };
}

export default useUrlState;
